import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { throwError } from 'rxjs';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService, PersonalService, SessionService, AuthService, FeedMessageService } from '@services/index.service';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@environments/environment';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { Consumer, CustomdataResponse, ErrorCode, LoginByCertificateRequest, MessageType, Response, CheckDomainRequest, LoginRequest, LocalStorageKeys, LoginStep, AngularPaths } from '@models/index.model';
import { Router } from '@angular/router';
import { BackgroundColor, InAppBrowser, OpenWebViewOptions, ToolBarType, UrlEvent } from '@capgo/inappbrowser';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false
})
export class LoginComponent implements OnInit {
  public theme = environment.THEME;
  public loginForm: UntypedFormGroup;
  public hide: boolean = true;
  // Custom login screen settings.
  public customImgSrc: string;
  public customText: string;
  // Login input fields.
  private formSubmitAttempt: boolean;
  public hostControl = new UntypedFormControl('', [
    Validators.required,
  ]);
  // Login steps management.
  public loginStep: LoginStep;
  public loadingCustomData = false;
  public loadingLogin = false;
  public loadingLoginCert = false;
  public isMobile: boolean;
  public currentConsumer: Consumer;
  public ssoLoginButtons = [];
  public ssoLoginClickedButton = '';
  public certificateDomainsConfigured: boolean = false;
  @ViewChild('password') public set focusPassword(password: ElementRef) {
    if (password) {
      password.nativeElement.focus();
      this._cdRef.detectChanges();
    }
  }

  constructor(
    public dialog: MatDialog,
    public translate: TranslateService,
    private router: Router,
    public _utils: UtilsService,
    private _fb: UntypedFormBuilder,
    private _session: SessionService,
    private _personalSvc: PersonalService,
    private _titleService: Title,
    private _auth: AuthService,
    private _notify: FeedMessageService,
    private _cdRef: ChangeDetectorRef
  ) {
    this.theme = this._utils.titleCaseWord(this.theme);
    this.isMobile = environment.platform === 'mobile';
    const mobileApiUrl = this._utils.getLocalStorage(LocalStorageKeys.MOBILE_HOST_URL) as string;

    if (this.isMobile) {
      if (!mobileApiUrl) {
        this.loginStep = LoginStep.HOST;
      }
      if (mobileApiUrl) {
        this.loginStep = LoginStep.USERNAME;
        this.hostControl.setValue(mobileApiUrl);
        this._getCustomData();
        this.getListDomains();
      }
      this.currentConsumer = Consumer.SIGNTRUST_APP;
    } else {
      this.loginStep = LoginStep.USERNAME;
      this.currentConsumer = Consumer.PERSONAL_AREA;
      this._getCustomData();
      this.getListDomains();
    }
  }

  public ngOnInit() {
    this.translate.get('LOGIN.TITLE-PAGE').subscribe(res => {
      this._titleService.setTitle(`${res} - ${this.theme}`);
    });
    this.loginForm = this._initLoginForm();
    this.dialog.closeAll();
  }

  public onSubmitHost(mobileApiUrl: string): void {
    if (mobileApiUrl[mobileApiUrl.length - 1] !== '/') {
      mobileApiUrl += '/';
    }
    this._utils.setLocalStorage(LocalStorageKeys.MOBILE_HOST_URL, mobileApiUrl);
    this._getCustomData();
    this.getListDomains();
    this.loginStep = LoginStep.USERNAME;
  }

  public onChangeHost(): void {
    this.loginStep = LoginStep.HOST;
  }

  public getListDomains() {
    this._personalSvc
      .personalSSODomain()
      .pipe(
        catchError(err => {
          this.resetForm();
          return throwError(() => err);
        }),
        finalize(() => {
        })
      )
      .subscribe((res: Response<any>) => {
        if (res.data) {
          this.ssoLoginButtons = res.data.ssoLoginButtons;
          this.certificateDomainsConfigured = res.data.certificateDomainsConfigured;
        }
      });
  }

  private _getCustomData() {
    this.loadingCustomData = true;
    this._personalSvc
      .getCustomData()
      .pipe(
        map((res: Response<CustomdataResponse>) => this._utils.mapResponseData<CustomdataResponse>(res)),
        finalize(() => {
          this.loadingCustomData = false;
        })
      )
      .subscribe((res: CustomdataResponse) => {
        this.customImgSrc = res.b64ImageData ? this._utils.parseText64ToImgURL(res.b64ImageData, 'png') : null;
        this.customText = res.customText ? res.customText : null;
      });
  }

  private _initLoginForm(): UntypedFormGroup {
    return this._fb.group({
      username: [null, Validators.required],
      password: [null, Validators.required],
      consumer: [this.currentConsumer],
      domainToReturn: [window.location.origin],
    });
  }

  public isFieldInvalid(field: string) {
    return (
      (!this.loginForm.get(field).valid && this.loginForm.get(field).touched) ||
      (this.loginForm.get(field).untouched && this.formSubmitAttempt)
    );
  }

  public onSubmitUsername({ username, consumer, domainToReturn }: CheckDomainRequest) {
    this.loadingLogin = true;
    this._auth
      .checkDomain({
        username: username,
        consumer: consumer,
        domainToReturn: domainToReturn,
      })
      .pipe(
        catchError(err => {
          this.resetForm();
          return throwError(() => err);
        }),
        finalize(() => {
          this.loadingLogin = false;
        })
      )
      .subscribe((res: Response<any>) => {
        if (res.errorCode === ErrorCode.OK) {
          this.loginStep = LoginStep.PASSWORD;
        }
        if (res.errorCode === ErrorCode.REDIRECT) {
          if (!res.data) {
            this._notify.feedBackResult(MessageType.ERROR, this.translate.instant('GLOBAL.ERROR-RETRY'));
            return;
          } else {
            if (this.isMobile) {
              // Signtrust APP open BrowserInApp
              this.openSSOAuth(res.data.url);
            } else {
              window.location.href = res.data.url;
            }
          }
        }
      });
  }

  public loginWithSso(domain) {
    this.loadingLogin = true;
    this._auth
      .checkDomain({
        username: domain,
        consumer: this.currentConsumer,
        domainToReturn: window.location.origin,
      })
      .pipe(
        catchError(err => {
          this.resetForm();
          return throwError(() => err);
        }),
        finalize(() => {
          this.loadingLogin = false;
        })
      )
      .subscribe((res: Response<any>) => {
        if (res.errorCode === ErrorCode.REDIRECT) {
          if (!res.data) {
            this._notify.feedBackResult(MessageType.ERROR, this.translate.instant('GLOBAL.ERROR-RETRY'));
            return;
          } else {
            if (this.isMobile) {
              // Signtrust APP open BrowserInApp
              this.openSSOAuth(res.data.url);
            } else {
              window.location.href = res.data.url;
            }
          }
        }
      });
  }

  public async openSSOAuth(url: string): Promise<void> {
    let webViewInApp: OpenWebViewOptions = {
      url: url,
      // headers: Headers,
      // shareDisclaimer: DisclaimerOptions,
      toolbarType: ToolBarType.DEFAULT,
      shareSubject: '',
      title: 'Auth SSO - Signtrust',
      backgroundColor: BackgroundColor.BLACK,
      activeNativeNavigationForWebview: false,
      disableGoBackOnNativeApplication: true,
      isPresentAfterPageLoad: false,
      isInspectable: false,
      isAnimated: true,
      showReloadButton: false,
      closeModal: false,
      closeModalTitle: 'Close',
      closeModalDescription: 'Are you sure you want to close this window?',
      closeModalOk: 'Close',
      closeModalCancel: 'Cancel',
      visibleTitle: true,
      toolbarColor: '#ffffff',
      showArrow: false
    }
    await InAppBrowser.openWebView(webViewInApp);

    InAppBrowser.addListener('urlChangeEvent', (info: UrlEvent) => {
      console.log('InAppBrowser urlChangeEvent: ', info.url);
      if (info.url.includes('/personal/login-by-token/')) {
        console.log('Auth LoginByToken Detect: ', info.url);
        // Extraer el código o token de la URL
        const urlSplit: string[] = info.url.split('login-by-token/');
        const tempToken = urlSplit[urlSplit.length - 1];
        console.log('Call AuthAPI LoginByToken:', tempToken);
        this.router.navigate([`/${AngularPaths.LOGIN_BY_TOKEN}/${tempToken}`]);
        // Cierra el navegador
        InAppBrowser.close();
      }
      if (info.url.includes('/personal/error/')) {
        console.log('Error LoginByToken Detect: ', info.url);
        // Extraer el código o token de la URL
        const urlSplit: string[] = info.url.split('error/');
        const customError = urlSplit[urlSplit.length - 1];
        console.log('Error on IDP Auth LoginByToken:', customError);
        this.router.navigate([`/${AngularPaths.ERROR}/${customError}`]);
        // Cierra el navegador
        InAppBrowser.close();
      }
    });

    InAppBrowser.addListener('closeEvent', (info: UrlEvent) => {
      console.log('InAppBrowser closeEvent: ', info.url);
      InAppBrowser.removeAllListeners();
    });
  }

  public onSubmitPassword({ username, password, consumer }: LoginRequest) {
    const passwordControl: AbstractControl = this.loginForm.get('password');
    passwordControl.setValue(passwordControl.value.trim());
    this.loadingLogin = true;
    this._session
      .initLocalUserSession({ username, password, consumer })
      .pipe(
        tap(_ => this.resetForm()),
        catchError(err => {
          this._resetPassword();
          this.formSubmitAttempt = true;
          return throwError(() => err);
        }),
        finalize(() => {
          this.loadingLogin = false;
        })
      )
      .subscribe();
  }

  public onLoginByCertificate() {
    this.loadingLoginCert = true;
    const loginByCertificateRequest: LoginByCertificateRequest = { consumer: this.currentConsumer };
    this._session
      .initCertificateSession(loginByCertificateRequest)
      .pipe(
        catchError(err => {
          return throwError(() => err);
        }),
        finalize(() => {
          this.resetForm();
          this.formSubmitAttempt = true;
          this.loadingLoginCert = false;
        })
      )
      .subscribe();
  }

  public resetForm() {
    this._resetUsername();
    this._resetPassword();
    this.loginStep = LoginStep.USERNAME;
  }

  private _resetUsername() {
    const userNameControl: AbstractControl = this.loginForm.get('username');
    userNameControl.reset();
    userNameControl.updateValueAndValidity();
  }

  private _resetPassword() {
    const passwordControl: AbstractControl = this.loginForm.get('password');
    passwordControl.reset();
    passwordControl.updateValueAndValidity();
  }
}
